import { createContext, FC, useContext } from 'react'

/**
 * Where the room's stage is being drawn. Tools ask this instead of knowing
 * about presenter mode (`presenter-mode/`), which provides it:
 *
 * - `room`: an ordinary window - the trainer's or a participant's;
 * - `console`: the trainer's main window while a presentation runs; the
 *   films and recordings play in the presentation window instead;
 * - `projector`: the presentation window - the stage only, nothing to click.
 */
export type StageSurface = 'room' | 'console' | 'projector'

const StageSurfaceContext = createContext<StageSurface>('room')

export const ProvideStageSurface: FC<{ surface: StageSurface }> = ({
	surface,
	children,
}) => (
	<StageSurfaceContext.Provider value={surface}>
		{children}
	</StageSurfaceContext.Provider>
)

export const useStageSurface = (): StageSurface =>
	useContext(StageSurfaceContext)

/**
 * The controls of a tool on the stage (close, play, End...): shown wherever
 * someone works the tool, never on the projector, which shows the content
 * only. They could not be used there anyway - the projector's stage is
 * `inert` - so this decides what the room sees, not what is safe.
 */
export const StageControls: FC = ({ children }) =>
	useStageSurface() === 'projector' ? null : <>{children}</>
